import { css } from "@emotion/react";
import { styleUtils } from "@minted/minted-components";
import React, { memo } from "react";

/**
 * Overlapping media treatment — two portrait photos staggered on a diagonal
 * (back top-left, front bottom-right) so they partially overlap. Each photo
 * flushes to its outer edge. Design theme does not style these images; layout
 * CSS only. Page/theme background shows through around the cluster.
 *
 * data-cy leftImage / rightImage match EditableHeader's modular pencil hooks
 * (slot 0 = back, slot 1 = front).
 */
const styles = {
  backPhoto: css`
    left: 0;
    position: absolute;
    top: 0;
    /* Wider than 50% so left- and right-flush photos still overlap. */
    width: 58%;
    z-index: 1;
  `,
  frontPhoto: css`
    position: absolute;
    right: 0;
    top: 28%;
    width: 58%;
    z-index: 2;
  `,
  image: css`
    aspect-ratio: 3 / 4;
    box-sizing: border-box;
    display: block;
    object-fit: cover;
    width: 100%;
  `,
  // Room for back (top-left) + front (offset) without clipping.
  // top % is of stage height H; front height is 58% × 4/3 of width W.
  // 0.28H + 0.773W = H → H ≈ 107.4% of W.
  stage: css`
    aspect-ratio: 100 / 107.4;
    margin-bottom: ${styleUtils.rem(30)};
    position: relative;
    width: 100%;
  `,
  wrapper: css`
    box-sizing: border-box;
    margin-left: auto;
    margin-right: auto;
    max-width: ${styleUtils.rem(1170)};
    padding-left: ${styleUtils.rem(15)};
    padding-right: ${styleUtils.rem(15)};
  `,
};

export type OverlappingMediaProps = {
  alt: string;
  backPhotoURL: string;
  frontPhotoURL: string;
};

const OverlappingMedia = ({
  alt,
  backPhotoURL,
  frontPhotoURL,
}: OverlappingMediaProps) => (
  <div css={styles.wrapper}>
    <div css={styles.stage}>
      <div css={styles.backPhoto}>
        <img
          alt={alt}
          css={styles.image}
          data-cy="leftImage"
          src={backPhotoURL}
        />
      </div>
      <div css={styles.frontPhoto}>
        <img
          alt={alt}
          css={styles.image}
          data-cy="rightImage"
          src={frontPhotoURL}
        />
      </div>
    </div>
  </div>
);

const MemoizedOverlappingMedia = memo(OverlappingMedia);

export { MemoizedOverlappingMedia as OverlappingMedia };
